import React, { Component } from 'react';
import axios from "../../utils/axiosInstance"; // Adjust path to match your project setup
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './HomeSearchBar.css';

class HomeSearchBar extends Component {
  
  constructor(props) {
    super(props);
    
    const initialViewDate = new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      1
    ); 

    this.state = {
      activeTab: 'Rooms',
      where: '',
      checkIn: null, 
      checkOut: null, 
      adults: 1,
      children: 0,
      rooms: 1,
      activeDropdown: null, 
      
      viewingDate: initialViewDate,
      selectionStart: null, 
      selectionEnd: null,

      // Live Search States
      searchResults: [],
      isSearching: false,
    };
    
    this.wrapperRef = React.createRef();
    this.searchTimeout = null;
    this.lastLocationQuery = null;
    this.locationRequestId = 0;
    this.locationRequestPending = false;
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  }

  handleClickOutside(event) {
    if (this.wrapperRef && !this.wrapperRef.current.contains(event.target)) {
      this.setState({ activeDropdown: null });
    }
  }

  handleTabChange = (tab) => {
    this.setState({ activeTab: tab, activeDropdown: null });
  };

  toggleDropdown = (dropdownName) => {
    this.setState((prevState) => ({
      activeDropdown: prevState.activeDropdown === dropdownName ? null : dropdownName
    }));
  };

  // --- Search Logic ---

  formatDateForURL = (dateObj) => {
    if (!dateObj) return "";
    const m = `${dateObj.getMonth() + 1}`.padStart(2, "0");
    const d = `${dateObj.getDate()}`.padStart(2, "0");
    return `${dateObj.getFullYear()}-${m}-${d}`;
  };

  handleSearch = () => {
    const { activeTab, where, checkIn, checkOut, rooms, adults, children } = this.state;
    
    const startDate = this.formatDateForURL(checkIn);
    const endDate = this.formatDateForURL(checkOut);
    const tabParam = activeTab.toLowerCase(); 
    
    window.location.href = `/q?t=${tabParam}&a=${encodeURIComponent(where)}&s=${startDate}&e=${endDate}&r=${rooms}&ad=${adults}&k=${children}`;
  };

  // --- Live Search & Location Handlers ---

  // --- Database-backed destination search ---

  openLocationDropdown = () => {
    const query = (this.state.where || '').trim();
    this.setState({ activeDropdown: 'location' });

    // Load default suggestions from the API when the field opens empty.
    if (this.lastLocationQuery !== query) {
      this.fetchLocations(query);
    }
  };

  handleLocationChange = (e) => {
    const value = e.target.value;

    if (this.searchTimeout) clearTimeout(this.searchTimeout);

    // Invalidate any older request so stale results cannot replace newer ones.
    this.locationRequestId += 1;
    this.locationRequestPending = false;
    this.lastLocationQuery = null;

    this.setState({
      where: value,
      activeDropdown: 'location',
      searchResults: [],
      isSearching: true,
    });

    // Debounce requests while the user types. Empty text also calls the API
    // with q= so default suggestions are loaded from the database.
    this.searchTimeout = setTimeout(() => {
      this.fetchLocations(value);
    }, 350);
  };

  // Press Enter to accept the text currently in the Where field and continue.
  handleLocationKeyDown = (e) => {
    if (e.key !== 'Enter') return;

    const query = (this.state.where || '').trim();
    if (!query) return;

    e.preventDefault();
    if (this.searchTimeout) clearTimeout(this.searchTimeout);

    // Ignore any older API response after the user commits the typed address.
    this.locationRequestId += 1;
    this.locationRequestPending = false;
    this.lastLocationQuery = null;

    this.setState({
      activeDropdown: 'calendar',
      searchResults: [],
      isSearching: false,
    });
  };

  fetchLocations = async (query = '') => {
    const normalizedQuery = String(query ?? '').trim();

    // Prevent duplicate calls caused by both click and input focus events.
    if (this.lastLocationQuery === normalizedQuery) return;

    this.lastLocationQuery = normalizedQuery;
    this.locationRequestPending = true;
    const requestId = ++this.locationRequestId;
    this.setState({ isSearching: true });

    try {
      const res = await axios.get('/search_addresses', {
        params: { q: normalizedQuery },
      });

      // Ignore responses from requests that are no longer current.
      if (requestId !== this.locationRequestId) return;

      const items = Array.isArray(res?.data?.data) ? res.data.data : [];
      const searchResults = items
        .map((item) => {
          if (typeof item === 'string') return { title: item };
          if (item && (item.title || item.address)) {
            return { ...item, title: item.title || item.address };
          }
          return null;
        })
        .filter((item) => item && String(item.title).trim() !== '');

      this.locationRequestPending = false;
      this.setState({ searchResults, isSearching: false });
    } catch (error) {
      if (requestId !== this.locationRequestId) return;

      console.error('Location search failed:', error);
      this.locationRequestPending = false;
      this.lastLocationQuery = null; // Allow another attempt on the next focus.
      this.setState({ searchResults: [], isSearching: false });
    }
  };

  clearLocation = (e) => {
    e.stopPropagation();

    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.locationRequestId += 1;
    this.locationRequestPending = false;
    this.lastLocationQuery = null;

    this.setState({
      where: '',
      activeDropdown: 'location',
      searchResults: [],
      isSearching: true,
    });

    // Explicitly fetch q= because focusing an already-focused input does not
    // always fire another onFocus event.
    setTimeout(() => {
      this.inputRef?.focus();
      this.fetchLocations('');
    }, 0);
  };

  handleLocationSelect = (place, e) => {
    if (e) e.stopPropagation();

    const placeValue = typeof place === 'object'
      ? (place?.title || place?.address || '')
      : String(place ?? '');

    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.locationRequestId += 1;
    this.locationRequestPending = false;
    this.lastLocationQuery = null;

    this.setState({
      where: placeValue,
      activeDropdown: 'calendar',
      searchResults: [],
      isSearching: false,
    });
  };

  // --- Guest Handlers ---

  handleGuestChange = (type, operation, e) => {
    e.stopPropagation(); 
    this.setState((prevState) => {
      let newValue = prevState[type];
      if (operation === 'increment') newValue++;
      if (operation === 'decrement' && newValue > 0) {
        if ((type === 'adults' || type === 'rooms') && newValue === 1) return null; 
        newValue--;
      }
      return { [type]: newValue };
    });
  };

  closeGuestDropdown = (e) => {
    e.stopPropagation();
    this.setState({ activeDropdown: null });
  };

  getGuestSummary = () => {
    const { adults, children, rooms, activeTab } = this.state;
    let summary = `${adults} Adult${adults > 1 ? 's' : ''} • ${children} Children`;
    if (activeTab === 'Hotels') summary += ` • ${rooms} Room${rooms > 1 ? 's' : ''}`;
    return summary;
  };

  // --- Calendar Handlers ---

  getTodayDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  changeMonth = (increment, e) => {
    e.stopPropagation();
    this.setState((prevState) => {
      const newDate = new Date(prevState.viewingDate);
      newDate.setMonth(newDate.getMonth() + increment);

      // Do not allow navigating to a month before the current month.
      const today = this.getTodayDate();
      const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      if (newDate < currentMonth) return null;

      return { viewingDate: newDate };
    });
  };

  handleDateClick = (date, e) => {
    e.stopPropagation();

    // Past dates cannot be selected, even if this handler is called directly.
    if (!(date instanceof Date) || Number.isNaN(date.getTime()) || date < this.getTodayDate()) {
      return;
    }

    const { selectionStart, selectionEnd } = this.state;

    if (selectionStart && selectionEnd) {
      this.setState({ selectionStart: date, selectionEnd: null });
      return;
    }

    if (!selectionStart) {
      this.setState({ selectionStart: date });
    } else {
      if (date < selectionStart) {
        this.setState({ selectionStart: date, selectionEnd: selectionStart });
      } else {
        this.setState({ selectionEnd: date });
      }
    }
  };

  handleClearDates = (e) => {
    e.stopPropagation();
    this.setState({
      selectionStart: null,
      selectionEnd: null,
      checkIn: null,
      checkOut: null
    });
  };

  handleOKDates = (e) => {
    e.stopPropagation();
    const { selectionStart, selectionEnd } = this.state;
    if (selectionStart && selectionEnd) {
      this.setState({
        checkIn: selectionStart,
        checkOut: selectionEnd,
        activeDropdown: 'guests'
      });
    }
  };

  generateCalendarDays = (year, month) => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    let days = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ day: daysInPrevMonth - i, isCurrentMonth: false, date: null });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, isCurrentMonth: true, date: new Date(year, month, i) });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ day: i, isCurrentMonth: false, date: null });
    }
    return days;
  };

  formatDisplayDate = (dateObj) => {
    if (!dateObj) return null;
    return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // --- Render Methods ---

  renderLocationDropdown = () => {
    const { where, searchResults, isSearching } = this.state;
    const queryIsEmpty = where.trim() === '';
    const hasResults = Array.isArray(searchResults) && searchResults.length > 0;

    return (
      <div
        className="custom-popover location-popover shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 popover-scrollable destination-scrollable">
          {isSearching ? (
            <div className="destination-state text-center py-3" role="status" aria-live="polite">
              <span className="spinner-border spinner-border-sm text-primary me-2" aria-hidden="true"></span>
              {queryIsEmpty ? 'Loading suggested destinations...' : 'Searching destinations...'}
            </div>
          ) : hasResults ? (
            <>
              <p className="destination-heading">
                {queryIsEmpty ? 'Suggested destinations' : 'Matching destinations'}
              </p>

              {queryIsEmpty && (
                <button
                  type="button"
                  className="destination-item destination-nearby"
                  onClick={(e) => this.handleLocationSelect('Nearby', e)}
                >
                  <span className="destination-icon nearby-icon">
                    <i className="bi bi-cursor" aria-hidden="true"></i>
                  </span>
                  <span className="destination-copy">
                    <span className="destination-name">Nearby</span>
                    <span className="destination-description">Find places around you</span>
                  </span>
                </button>
              )}

              {searchResults.map((item, index) => {
                const title = typeof item === 'string'
                  ? item
                  : (item?.title || item?.address || '');
                if (!String(title).trim()) return null;

                return (
                  <button
                    type="button"
                    key={item?.id ?? `${title}-${index}`}
                    className="destination-item"
                    onClick={(e) => this.handleLocationSelect(title, e)}
                  >
                    <span className="destination-icon">
                      <i className="bi bi-geo-alt" aria-hidden="true"></i>
                    </span>
                    <span className="destination-name">{title}</span>
                    <i className="bi bi-arrow-up-left destination-select-icon" aria-hidden="true"></i>
                  </button>
                );
              })}
            </>
          ) : (
            <div className="destination-state text-center py-3">
              <i className="bi bi-geo-alt destination-empty-icon" aria-hidden="true"></i>
              <div className="fw-semibold mt-2">
                {queryIsEmpty ? 'No suggested destinations available' : 'No matching destinations found'}
              </div>
              {!queryIsEmpty && (
                <div className="small mt-1">Try another place name.</div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  renderCalendarGrid = (year, month) => {
    const days = this.generateCalendarDays(year, month);
    const { selectionStart, selectionEnd } = this.state;
    const sTime = selectionStart ? selectionStart.getTime() : null;
    const eTime = selectionEnd ? selectionEnd.getTime() : null;
    const today = this.getTodayDate();

    return (
      <div className="month-grid">
        <div className="day-header text-muted">Su</div><div className="day-header text-muted">Mo</div>
        <div className="day-header text-muted">Tu</div><div className="day-header text-muted">We</div>
        <div className="day-header text-muted">Th</div><div className="day-header text-muted">Fr</div>
        <div className="day-header text-muted">Sa</div>
        
        {days.map((d, i) => {
          let cName = "cal-day";
          const isPastDate = d.isCurrentMonth && d.date < today;

          if (!d.isCurrentMonth) {
            cName += " text-muted";
          } else if (isPastDate) {
            cName += " disabled-date";
          } else {
            const dTime = d.date.getTime();
            if (sTime && dTime === sTime) cName += " selected-start";
            if (eTime && dTime === eTime) cName += " selected-end";
            if (sTime && eTime && dTime > sTime && dTime < eTime) cName += " selected-mid";
            cName += " cursor-pointer";
          }

          return (
            <div
              key={i}
              className={cName}
              aria-disabled={!d.isCurrentMonth || isPastDate}
              onClick={(e) => {
                if (d.isCurrentMonth && !isPastDate) {
                  this.handleDateClick(d.date, e);
                } else {
                  e.stopPropagation();
                }
              }}
            >
              <span>{d.day}</span>
            </div>
          );
        })}
      </div>
    );
  };

  renderCalendarDropdown = () => {
    const { viewingDate, selectionStart, selectionEnd } = this.state;
    
    const year1 = viewingDate.getFullYear();
    const month1 = viewingDate.getMonth();
    const date2 = new Date(year1, month1 + 1, 1);
    const year2 = date2.getFullYear();
    const month2 = date2.getMonth();

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    return (
      <div className="custom-popover calendar-popover shadow-lg p-4" onClick={(e) => e.stopPropagation()}>
        <div className="d-flex justify-content-between align-items-center mb-4 px-3">
          <button
            className="btn btn-link text-muted p-0 border-0 calendar-nav-btn"
            onClick={(e) => this.changeMonth(-1, e)}
            disabled={year1 === this.getTodayDate().getFullYear() && month1 === this.getTodayDate().getMonth()}
            aria-label="Previous month"
          >
            <i className="bi bi-chevron-left fs-5"></i>
          </button>
          <div className="d-flex gap-5">
            <span className="fw-bold" style={{ width: '180px', textAlign: 'center' }}>{monthNames[month1]} {year1}</span>
            <span className="fw-bold" style={{ width: '180px', textAlign: 'center' }}>{monthNames[month2]} {year2}</span>
          </div>
          <button className="btn btn-link text-muted p-0 border-0" onClick={(e) => this.changeMonth(1, e)}>
            <i className="bi bi-chevron-right fs-5"></i>
          </button>
        </div>

        <div className="calendar-months-container d-flex gap-4 mb-3">
          {this.renderCalendarGrid(year1, month1)}
          {this.renderCalendarGrid(year2, month2)}
        </div>

        <div className="d-flex justify-content-between align-items-center pt-3 border-top">
          <span className="text-muted small">
            {selectionStart ? this.formatDisplayDate(selectionStart) : 'Start date'} – {selectionEnd ? this.formatDisplayDate(selectionEnd) : 'End date'}
          </span>
          <div className="d-flex gap-3 align-items-center">
            <span className="text-decoration-underline cursor-pointer small" onClick={this.handleClearDates}>Clear dates</span>
            <button 
              className="btn btn-primary px-4 py-1 rounded-pill" 
              onClick={this.handleOKDates}
              disabled={!selectionStart || !selectionEnd}
              style={{ backgroundColor: "#0049b0"}}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    );
  };

  renderGuestDropdown = () => {
    const { adults, children, rooms, activeTab } = this.state;
    return (
      <div className="custom-popover guest-popover shadow-lg p-3" onClick={(e) => e.stopPropagation()}>
        <div className="guest-counter-row d-flex justify-content-between align-items-center mb-3">
          <span className="fw-medium">Adults</span>
          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-outline-secondary rounded-circle counter-btn" onClick={(e) => this.handleGuestChange('adults', 'decrement', e)}><i className="bi bi-dash" style={{marginLeft: "-3px"}}></i></button>
            <span className="counter-val">{adults}</span>
            <button className="btn btn-outline-secondary rounded-circle counter-btn" onClick={(e) => this.handleGuestChange('adults', 'increment', e)}><i className="bi bi-plus" style={{marginLeft: "-3px"}}></i></button>
          </div>
        </div>

        <div className="guest-counter-row d-flex justify-content-between align-items-center mb-3">
          <span className="fw-medium">Children</span>
          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-outline-secondary rounded-circle counter-btn" onClick={(e) => this.handleGuestChange('children', 'decrement', e)}><i className="bi bi-dash" style={{marginLeft: "-3px"}}></i></button>
            <span className="counter-val">{children}</span>
            <button className="btn btn-outline-secondary rounded-circle counter-btn" onClick={(e) => this.handleGuestChange('children', 'increment', e)}><i className="bi bi-plus" style={{marginLeft: "-3px"}}></i></button>
          </div>
        </div>

        {activeTab === 'Hotels' && (
          <div className="guest-counter-row d-flex justify-content-between align-items-center mb-4">
            <span className="fw-medium">Rooms</span>
            <div className="d-flex align-items-center gap-3">
              <button className="btn btn-outline-secondary rounded-circle counter-btn" onClick={(e) => this.handleGuestChange('rooms', 'decrement', e)}><i className="bi bi-dash" style={{marginLeft: "-3px"}}></i></button>
              <span className="counter-val">{rooms}</span>
              <button className="btn btn-outline-secondary rounded-circle counter-btn" onClick={(e) => this.handleGuestChange('rooms', 'increment', e)}><i className="bi bi-plus" style={{marginLeft: "-3px"}}></i></button>
            </div>
          </div>
        )}

        <button className="btn btn-primary w-100 py-2 rounded-3 fw-medium" onClick={this.closeGuestDropdown}>
          Done
        </button>
      </div>
    );
  };

  render() {
    const { activeTab, where, checkIn, checkOut, activeDropdown } = this.state;
    const formattedCheckIn = this.formatDisplayDate(checkIn) || 'Select Date';
    const formattedCheckOut = this.formatDisplayDate(checkOut) || 'Select Date';

    return (
      <div 
        style={{ fontSize: '11px', marginTop: '-30px', position: 'relative', zIndex: 10, backgroundColor: '#d8e0e6' }} 
        className="travel-hero-section pb-5" 
        ref={this.wrapperRef}
      >
        <div className="container pt-5">
          <div className="d-flex justify-content-center">
            <div className="tab-container d-flex bg-light rounded-top shadow-sm overflow-hidden">
              <button className={`tab-btn ${activeTab === 'Rooms' ? 'active' : ''}`} onClick={() => this.handleTabChange('Rooms')}>
                <i className="bi bi-house-door me-2"></i>Rooms
              </button>
              <button className={`tab-btn ${activeTab === 'Apartments' ? 'active' : ''}`} onClick={() => this.handleTabChange('Apartments')}>
                <i className="bi bi-building me-2"></i>Apartments
              </button>
              <button className={`tab-btn ${activeTab === 'Hotels' ? 'active' : ''}`} onClick={() => this.handleTabChange('Hotels')}>
                <i className="bi bi-buildings me-2"></i>Hotels
              </button>
            </div>
          </div>

          <div className="search-bar-container bg-white p-3 rounded-4 shadow-lg d-flex gap-3 position-relative">
            
            <div className="search-field flex-grow-1 position-relative" onClick={this.openLocationDropdown}>
              <label className="fw-bold text-dark mb-1 d-block">Where</label>
              <div className="d-flex align-items-center text-muted position-relative">
                <i className="bi bi-geo-alt me-2"></i>
                <input 
                  type="text" 
                  ref={(input) => { this.inputRef = input; }}
                  className="search-input text-truncate w-100 bg-transparent border-0 pe-4" 
                  placeholder={`Where are you going? ${activeTab}`} 
                  value={where}
                  onChange={this.handleLocationChange}
                  onFocus={this.openLocationDropdown}
                  onKeyDown={this.handleLocationKeyDown}
                />
                {where && (
                  <i 
                    className="bi bi-x-circle-fill clear-input-icon text-muted cursor-pointer position-absolute" 
                    style={{ right: '5px', zIndex: 5, fontSize: '1.1rem' }}
                    onClick={this.clearLocation}
                  ></i>
                )}
              </div>
              {activeDropdown === 'location' && this.renderLocationDropdown()}
            </div>

            <div className="search-field position-relative" onClick={() => this.toggleDropdown('calendar')}>
              <label className="fw-bold text-dark mb-1 d-block">Check-in</label>
              <div className="d-flex align-items-center text-muted">
                <i className="bi bi-calendar me-2"></i>
                <span>{formattedCheckIn}</span>
              </div>
            </div>

            <div className="search-field position-relative" onClick={() => this.toggleDropdown('calendar')}>
              <label className="fw-bold text-dark mb-1 d-block">Check-out</label>
              <div className="d-flex align-items-center text-muted">
                <i className="bi bi-calendar me-2"></i>
                <span>{formattedCheckOut}</span>
              </div>
              {activeDropdown === 'calendar' && this.renderCalendarDropdown()}
            </div>

            <div className="search-field position-relative" onClick={() => this.toggleDropdown('guests')}>
              <label className="fw-bold text-dark mb-1 d-block">Guests</label>
              <div className="d-flex align-items-center text-muted">
                <i className="bi bi-person me-2"></i>
                <span>{this.getGuestSummary()}</span>
              </div>
              {activeDropdown === 'guests' && this.renderGuestDropdown()}
            </div>

            <div className="search-button-container d-flex align-items-center">
              <button 
                style={{ fontSize: '11px', backgroundColor: '#0047cc', borderRadius: '10px' }} 
                className="btn btn-primary search-btn d-flex align-items-center gap-2 h-100 px-4 fw-medium border-0"
                onClick={this.handleSearch}>
                <i className="bi bi-search"></i>
                Search {activeTab}
              </button>
            </div>

          </div>
        </div>
      </div>
    );
  }
}

export default HomeSearchBar;